import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { useJourneyStore } from '@/stores/journeyStore'
import { Brain, ArrowRight, Users, Building, Zap, Mail } from 'lucide-react'
import { analyticsEvents } from '@/lib/analytics'

interface OnboardingData {
  name: string
  email: string
  emailConsent: boolean
  businessType: string
  industry: string
  teamSize: number
  currentAIExperience: 'none' | 'basic' | 'intermediate' | 'advanced'
  primaryGoals: string[]
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const { initializeUser } = useJourneyStore()
  const [step, setStep] = useState(0)
  const [data, setData] = useState<OnboardingData>({
    name: '',
    email: '',
    emailConsent: false,
    businessType: '',
    industry: '',
    teamSize: 1,
    currentAIExperience: 'none',
    primaryGoals: []
  })

  const handleStart = async () => {
    try {
      await initializeUser(data)
      navigate('/dashboard')  // Redirect to dashboard after onboarding
    } catch (error) {
      console.error('Failed to initialize user:', error)
      // Still navigate to dashboard even if database save fails
      navigate('/dashboard')
    }
  }

  const businessTypes = [
    'Sole Proprietorship',
    'Small Business (2-10 employees)',
    'Medium Business (11-50 employees)',
    'Large Business (50+ employees)',
    'Startup',
    'Consultant/Freelancer',
    'Non-profit',
    'Other'
  ]

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail/E-commerce',
    'Manufacturing',
    'Professional Services',
    'Education',
    'Real Estate',
    'Marketing/Advertising',
    'Food & Beverage',
    'Other'
  ]

  const experienceLevels = [
    { value: 'none', label: 'Complete Beginner', description: 'Never used AI tools before' },
    { value: 'basic', label: 'Basic User', description: 'Used ChatGPT or similar occasionally' },
    { value: 'intermediate', label: 'Regular User', description: 'Use AI tools weekly for various tasks' },
    { value: 'advanced', label: 'Power User', description: 'Integrate AI into daily workflows' }
  ]

  const goalOptions = [
    'Increase efficiency and productivity',
    'Reduce operational costs',
    'Improve customer service',
    'Automate repetitive tasks',
    'Generate better content/marketing',
    'Make data-driven decisions',
    'Stay competitive with technology',
    'Explore new business opportunities'
  ]

  if (step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
        {/* Simple Header */}
        <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landing')}>
                <div className="p-2 rounded-lg bg-white shadow-sm">
                  <img 
                    src="/ekow-logo.png" 
                    alt="Ekow Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
                  <p className="text-sm text-muted-foreground">From Curious to Capable</p>
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate('/landing')}>
                Back to Home
              </Button>
            </div>
          </div>
        </header>
        
        <div className="flex items-center justify-center p-4 pt-8">
          <div className="max-w-4xl w-full">
          <Card className="text-center">
            <CardHeader className="pb-8">
              <div className="mx-auto mb-6 p-4 bg-primary/10 rounded-full w-20 h-20 flex items-center justify-center">
                <Brain className="w-10 h-10 text-primary" />
              </div>
              <CardTitle className="text-4xl mb-4">Welcome to Your AI Journey</CardTitle>
              <CardDescription className="text-lg max-w-2xl mx-auto">
                Experience the transformation from AI curious to AI capable through 5 hands-on stages. 
                Each stage builds your confidence and skills through real interaction, not just theory.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold">Interactive Learning</h3>
                  <p className="text-sm text-muted-foreground">
                    Hands-on practice with real AI tools, not just presentations
                  </p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold">Business Focused</h3>
                  <p className="text-sm text-muted-foreground">
                    Every exercise connects to real business scenarios and outcomes
                  </p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold">Personalized Results</h3>
                  <p className="text-sm text-muted-foreground">
                    Get custom AI recommendations based on your unique journey
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-8">
                <Button 
                  onClick={() => {
                    analyticsEvents.onboardingStarted()
                    setStep(1)
                  }}
                  size="lg"
                  className="px-8 py-3 text-lg gap-2"
                >
                  Start Your Journey <ArrowRight className="w-5 h-5" />
                </Button>
                <p className="text-sm text-muted-foreground mt-4">
                  Takes about 45-60 minutes • Progress is automatically saved
                </p>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    )
  }

  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
        {/* Simple Header */}
        <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landing')}>
                <div className="p-2 rounded-lg bg-white shadow-sm">
                  <img 
                    src="/ekow-logo.png" 
                    alt="Ekow Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
                  <p className="text-sm text-muted-foreground">From Curious to Capable</p>
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate('/landing')}>
                Back to Home
              </Button>
            </div>
          </div>
        </header>
        
        <div className="flex items-center justify-center p-4 pt-8">
          <div className="max-w-2xl w-full">
          <Card>
            <CardHeader>
              <CardTitle>Tell us about yourself</CardTitle>
              <CardDescription>
                This helps us personalize your experience and recommendations
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name</label>
                <input
                  type="text"
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address (Optional)
                </label>
                <input
                  type="email"
                  value={data.email}
                  onChange={(e) => setData({ ...data, email: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your email (optional)"
                />
                <div className="mt-3 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="emailConsent"
                    checked={data.emailConsent}
                    onChange={(e) => setData({ ...data, emailConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 rounded border-input focus:ring-2 focus:ring-primary"
                  />
                  <label htmlFor="emailConsent" className="text-sm text-muted-foreground leading-relaxed">
                    📧 Send me personalized AI tools recommendations and insights based on my journey (optional)
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Business Type</label>
                <select
                  value={data.businessType}
                  onChange={(e) => setData({ ...data, businessType: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select business type</option>
                  {businessTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Industry</label>
                <select
                  value={data.industry}
                  onChange={(e) => setData({ ...data, industry: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(0)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(2)}
                  disabled={!data.name || !data.businessType || !data.industry}
                  className="flex-1"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    )
  }

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
        {/* Simple Header */}
        <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landing')}>
                <div className="p-2 rounded-lg bg-white shadow-sm">
                  <img 
                    src="/ekow-logo.png" 
                    alt="Ekow Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
                  <p className="text-sm text-muted-foreground">From Curious to Capable</p>
                </div>
              </div>
              <Button variant="ghost" onClick={() => navigate('/landing')}>
                Back to Home
              </Button>
            </div>
          </div>
        </header>
        
        <div className="flex items-center justify-center p-4 pt-8">
          <div className="max-w-2xl w-full">
          <Card>
            <CardHeader>
              <CardTitle>What's your current AI experience?</CardTitle>
              <CardDescription>
                This helps us adjust the difficulty and pacing for you
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {experienceLevels.map(level => (
                <div
                  key={level.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-primary/50 ${
                    data.currentAIExperience === level.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border'
                  }`}
                  onClick={() => setData({ ...data, currentAIExperience: level.value as any })}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 mt-0.5 ${
                      data.currentAIExperience === level.value
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    }`} />
                    <div>
                      <h4 className="font-medium">{level.label}</h4>
                      <p className="text-sm text-muted-foreground">{level.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-4 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!data.currentAIExperience}
                  className="flex-1"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    )
  }

  // Step 3: Goals selection and final setup
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      {/* Simple Header */}
      <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landing')}>
              <div className="p-2 rounded-lg bg-white shadow-sm">
                <img 
                  src="/ekow-logo.png" 
                  alt="Ekow Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
                <p className="text-sm text-muted-foreground">From Curious to Capable</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => navigate('/landing')}>
              Back to Home
            </Button>
          </div>
        </div>
      </header>
      
      <div className="flex items-center justify-center p-4 pt-8">
        <div className="max-w-2xl w-full">
        <Card>
          <CardHeader>
            <CardTitle>What are your primary goals?</CardTitle>
            <CardDescription>
              Select all that apply. This helps us customize your recommendations.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              {goalOptions.map(goal => (
                <div
                  key={goal}
                  className={`p-3 border rounded-lg cursor-pointer transition-all hover:border-primary/50 ${
                    data.primaryGoals.includes(goal)
                      ? 'border-primary bg-primary/5' 
                      : 'border-border'
                  }`}
                  onClick={() => {
                    const goals = data.primaryGoals.includes(goal)
                      ? data.primaryGoals.filter(g => g !== goal)
                      : [...data.primaryGoals, goal]
                    setData({ ...data, primaryGoals: goals })
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border ${
                      data.primaryGoals.includes(goal)
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    }`} />
                    <span className="text-sm">{goal}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 pt-6">
              <Button 
                variant="outline" 
                onClick={() => setStep(2)}
                className="flex-1"
              >
                Back
              </Button>
              <Button 
                onClick={handleStart}
                disabled={data.primaryGoals.length === 0}
                className="flex-1 gap-2"
              >
                Begin Journey <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  )
}